import { useCallback, useState } from "react";
import type {
  StripeCardNumberElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardCvcElementChangeEvent,
} from "@stripe/stripe-js";
import { toCardBrand, CardBrand, CardFieldErrors } from "./types";

export interface UseCardInputReturn {
  cardholderName: string;
  setCardholderName: (v: string) => void;
  detectedBrand: CardBrand;
  cardNumberComplete: boolean;
  expiryComplete: boolean;
  cvcComplete: boolean;
  /** Bumped each time a card number is completed, so checks tied to the card can tell cards apart. */
  cardNumberRevision: number;
  fieldErrors: CardFieldErrors;
  handleCardNumberChange: (e: StripeCardNumberElementChangeEvent) => void;
  handleExpiryChange: (e: StripeCardExpiryElementChangeEvent) => void;
  handleCvcChange: (e: StripeCardCvcElementChangeEvent) => void;
  validateFields: () => boolean;
  reset: () => void;
}

export function useCardInput(): UseCardInputReturn {
  const [cardholderName, setCardholderName] = useState("");
  const [detectedBrand, setDetectedBrand] = useState<CardBrand>("unknown");
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [expiryComplete, setExpiryComplete] = useState(false);
  const [cvcComplete, setCvcComplete] = useState(false);
  const [cardNumberRevision, setCardNumberRevision] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<CardFieldErrors>({});

  const handleCardNumberChange = useCallback((e: StripeCardNumberElementChangeEvent) => {
    setDetectedBrand(toCardBrand(e.brand));
    setCardNumberComplete(e.complete);
    if (e.complete) {
      // Bumped only on completion so typing doesn't re-render the checkout on every keystroke.
      setCardNumberRevision((r) => r + 1);
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.cardNumber;
        return next;
      });
    } else if (e.error) {
      setFieldErrors((prev) => ({
        ...prev,
        cardNumber: e.error?.message ?? "Invalid card number",
      }));
    }
  }, []);

  const handleExpiryChange = useCallback((e: StripeCardExpiryElementChangeEvent) => {
    setExpiryComplete(e.complete);
    if (e.complete) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.expiry;
        return next;
      });
    } else if (e.error) {
      setFieldErrors((prev) => ({
        ...prev,
        expiry: e.error?.message ?? "Enter a valid date",
      }));
    }
  }, []);

  const handleCvcChange = useCallback((e: StripeCardCvcElementChangeEvent) => {
    setCvcComplete(e.complete);
    if (e.complete) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.cvc;
        return next;
      });
    } else if (e.error) {
      setFieldErrors((prev) => ({
        ...prev,
        cvc: e.error?.message ?? "Invalid CVC",
      }));
    }
  }, []);

  const reset = useCallback(() => {
    setCardholderName("");
    setDetectedBrand("unknown");
    setCardNumberComplete(false);
    setExpiryComplete(false);
    setCvcComplete(false);
    setFieldErrors({});
  }, []);

  const validateFields = useCallback((): boolean => {
    const errs: CardFieldErrors = {};
    if (!cardholderName.trim()) errs.cardholderName = "Card holder name is required";
    if (!cardNumberComplete) errs.cardNumber = "Card number is required";
    if (!expiryComplete) errs.expiry = "Expiration date is required";
    if (!cvcComplete) errs.cvc = "CVV is required";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }, [cardholderName, cardNumberComplete, expiryComplete, cvcComplete]);

  return {
    cardholderName,
    setCardholderName,
    detectedBrand,
    cardNumberComplete,
    expiryComplete,
    cvcComplete,
    cardNumberRevision,
    fieldErrors,
    handleCardNumberChange,
    handleExpiryChange,
    handleCvcChange,
    validateFields,
    reset,
  };
}
